/**
 * Taxonomy Controller (Hono)
 *
 * Handlers for generic tag/assignment REST endpoints. Business logic lives
 * in `taxonomy.service.ts`; controllers only bridge HTTP <-> service.
 */

import type { Context } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv } from '../../http/types.js'
import type { TaxonomyService } from './taxonomy.service.js'

type C = Context<NexusEnv>

export function createTaxonomyController(ctx: ModuleContext) {
  const taxonomyService = ctx.services.get<TaxonomyService>('taxonomy')

  const getAuth = (c: C) => ({
    userId: c.get('user')?.id,
    ability: c.get('ability')
  })

  return {
    /**
     * GET /taxonomy/entities/:entityType/:entityId/tags
     */
    async listEntityTags(c: C): Promise<Response> {
      const entityType = c.req.param('entityType')!
      const entityId = c.req.param('entityId')!
      return c.json(await taxonomyService.getTagsForEntity(entityType, entityId))
    },

    /**
     * PUT /taxonomy/entities/:entityType/:entityId/tags
     */
    async syncEntityTags(c: C): Promise<Response> {
      const entityType = c.req.param('entityType')!
      const entityId = c.req.param('entityId')!
      const { tagIds } = (await c.req.json()) as { tagIds: string[] }
      return c.json(
        await taxonomyService.syncEntityTags(entityType, entityId, tagIds, getAuth(c))
      )
    },

    /**
     * POST /taxonomy/entities/:entityType/:entityId/tags
     */
    async assignEntityTag(c: C): Promise<Response> {
      const entityType = c.req.param('entityType')!
      const entityId = c.req.param('entityId')!
      const { tagId } = (await c.req.json()) as { tagId: string }
      await taxonomyService.assignEntityTag(entityType, entityId, tagId, getAuth(c))
      return c.json({ success: true }, 201)
    },

    /**
     * DELETE /taxonomy/entities/:entityType/:entityId/tags/:tagId
     */
    async unassignEntityTag(c: C): Promise<Response> {
      const entityType = c.req.param('entityType')!
      const entityId = c.req.param('entityId')!
      const tagId = c.req.param('tagId')!
      await taxonomyService.unassignEntityTag(entityType, entityId, tagId, getAuth(c))
      return c.body(null, 204)
    },

    /**
     * GET /taxonomy/stats
     */
    async getStats(c: C): Promise<Response> {
      const knex = ctx.db.knex
      const [tagCount, assignmentCount, entityTypesCount, orphanCount] = await Promise.all([
        knex('tags').count('* as count').first(),
        knex('tag_assignments').count('* as count').first(),
        knex('tag_assignments').countDistinct('entity_type as count').first(),
        knex('tags')
          .leftJoin('tag_assignments', 'tags.id', 'tag_assignments.tag_id')
          .whereNull('tag_assignments.id')
          .count('tags.id as count')
          .first()
      ])

      return c.json({
        total_tags: Number(tagCount?.['count'] ?? 0),
        total_assignments: Number(assignmentCount?.['count'] ?? 0),
        entity_types: Number(entityTypesCount?.['count'] ?? 0),
        orphan_tags: Number(orphanCount?.['count'] ?? 0)
      })
    },

    /**
     * GET /taxonomy/stats/by-type
     */
    async getStatsByType(c: C): Promise<Response> {
      const knex = ctx.db.knex
      const rows = await knex('tag_assignments')
        .select('entity_type')
        .count('* as count')
        .groupBy('entity_type')
        .orderBy('count', 'desc')
        .limit(20)

      const items = (rows as { entity_type: string; count: string | number }[]).map(r => ({
        x: r.entity_type,
        y: Number(r.count)
      }))
      return c.json(items)
    },

    /**
     * GET /taxonomy/stats/top-tags
     */
    async getStatsTopTags(c: C): Promise<Response> {
      const knex = ctx.db.knex
      const rows = await knex('tags')
        .select('tags.name', 'tags.color')
        .leftJoin('tag_assignments', 'tags.id', 'tag_assignments.tag_id')
        .count('tag_assignments.id as assignments')
        .groupBy('tags.id', 'tags.name', 'tags.color')
        .orderBy('assignments', 'desc')
        .limit(10)

      return c.json(
        rows.map(r => {
          const row = r as Record<string, unknown>
          return {
            name: row['name'],
            color: row['color'],
            assignments: Number(row['assignments'] ?? 0)
          }
        })
      )
    },

    /**
     * GET /taxonomy/tags/:tagId/entities
     */
    async listEntitiesForTag(c: C): Promise<Response> {
      const tagId = c.req.param('tagId')!
      const entityType = c.req.query('entityType')
      return c.json(await taxonomyService.getEntitiesForTag(tagId, entityType))
    }
  }
}
